import type { BotDifficulty } from './bot';
import type { GameEvent, InputMessage, Snapshot, Vec } from './types';

/**
 * Wire protocol: JSON messages over a plain WebSocket. The same messages are
 * handled by the Node server and the Cloudflare Durable Object.
 *
 * The client opens `/ws?room=<id>` and sends `join` first.
 */
export type ClientMessage =
  | { t: 'join'; name: string }
  | ({ t: 'input' } & InputMessage)
  /** `lead` = the client's one-way latency estimate (ms); the server advances the spawn by it, capped */
  | { t: 'shoot'; dir: Vec; lead?: number }
  | { t: 'ping'; n: number }
  | { t: 'shockwave' }
  /** ask the room to run this many bots (0–7); anyone in the room may */
  | { t: 'bots'; count: number; difficulty?: BotDifficulty };

export type ServerMessage =
  | {
      t: 'joined';
      id: string;
      room: string;
      serverTime: number;
      /** diagnostics: where the connection entered the edge and where the room runs (Cloudflare colos) */
      entryColo?: string;
      roomColo?: string;
    }
  | { t: 'snap'; s: Snapshot }
  | { t: 'event'; e: GameEvent }
  | { t: 'pong'; n: number };

export const WS_PATH = '/ws';

export function parseClientMessage(raw: unknown): ClientMessage | null {
  if (typeof raw !== 'string' || raw.length > 2048) return null;
  try {
    const m = JSON.parse(raw) as { t?: unknown };
    if (!m || typeof m !== 'object' || typeof m.t !== 'string') return null;
    switch (m.t) {
      case 'join':
      case 'input':
      case 'shoot':
      case 'ping':
      case 'bots':
      case 'shockwave':
        return m as ClientMessage;
      default:
        return null;
    }
  } catch {
    return null;
  }
}

export function sanitizeName(raw: unknown, maxLength: number): string {
  const fallback = `Orb${Math.floor(Math.random() * 1000)}`;
  if (typeof raw !== 'string') return fallback;
  const cleaned = raw.replace(/[<>]/g, '').replace(/\s+/g, ' ').trim().slice(0, maxLength);
  return cleaned || fallback;
}

export function sanitizeRoom(raw: unknown): string {
  if (typeof raw !== 'string') return 'lobby';
  const cleaned = raw.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 64);
  return cleaned || 'lobby';
}
